import {
	type UTMParameters,
	useUrlWithUtmParameters,
} from '@/lib/hooks/useUrlWithUtmParameters.ts'

export interface UTMLinkProps extends React.ComponentProps<'a'> {
	/**
	 * When `true`, UTM parameters from the current page URL are forwarded onto
	 * the rendered link (campaign attribution is preserved across navigations)
	 * and `utmParams` is ignored. If the current page has no UTM parameters,
	 * `utmParams` is used as a fallback.
	 */
	preserveUtmParams?: boolean
	/**
	 * UTM parameters to apply to the rendered link. Ignored when
	 * `preserveUtmParams` is `true` and the current page already carries UTM
	 * parameters.
	 */
	utmParams?: UTMParameters
}

/**
 * Anchor (`<a>`) that automatically decorates its `href` with UTM parameters.
 *
 * Behaves like a standard anchor — all native `<a>` props are forwarded — but
 * the rendered `href` is built via {@link useUrlWithUtmParameters}, applying
 * either the current page's UTM params (when `preserveUtmParams` is `true`) or
 * the provided `utmParams`.
 *
 * Relative `href` values are resolved against the current page URL. If `href`
 * is omitted, no `href` attribute is rendered (matching native anchor behavior
 * for non-link anchors).
 */
export const UTMLink: React.FC<UTMLinkProps> = ({
	children,
	href,
	preserveUtmParams,
	utmParams,
	...props
}: UTMLinkProps) => {
	const url = useUrlWithUtmParameters(href ?? '.', {
		preserveUtmParams,
		utmParams,
	})
	return (
		<a href={href !== undefined ? url.toString() : undefined} {...props}>
			{children}
		</a>
	)
}
